/**
 * The project journal - the complete transaction ledger.
 *
 * Sorting, filtering and paging all happen server-side through one endpoint, so the totals in
 * the footer always describe exactly the rows on screen. Selection drives bulk approval, which
 * is the moment a draft becomes part of the project's actuals.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  MagnifyingGlass, CaretUp, CaretDown, X, CheckCircle, Trash, PencilSimple, Paperclip,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { api } from '../../../context/AuthContext';
import { cn } from '../../../lib/utils';
import { phaseMeta, phaseLabel, phaseOptions, UNASSIGNED_PHASE } from '../../../lib/projectPhases';
import { money, STATUS_PILLS, SOURCE_LABELS, monthLabel, toCsv, downloadCsv } from './financeFormat';
import { JournalEntryPanel } from './JournalEntryPanel';

const PAGE_SIZE = 25;

const SortHeader = ({ label, field, sort, onSort, align = 'left' }) => (
  <th
    className={cn(
      'py-2 px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted cursor-pointer select-none whitespace-nowrap',
      align === 'right' ? 'text-right' : 'text-left'
    )}
    onClick={() => onSort(field)}
    data-testid={`journal-sort-${field}`}
  >
    <span className={cn('inline-flex items-center gap-1', align === 'right' && 'flex-row-reverse')}>
      {label}
      {sort.by === field &&
        (sort.dir === 'asc' ? <CaretUp className="h-3 w-3" weight="bold" /> : <CaretDown className="h-3 w-3" weight="bold" />)}
    </span>
  </th>
);

export const FinanceJournal = ({ projectId, projectType, currency = 'EUR', onChanged, initialFilters, onEdit }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({ costs: { budget: 0, actual: 0 }, income: { budget: 0, actual: 0 } });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState({ by: 'entry_date', dir: 'desc' });
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: '', entry_type: '', category: '', phase: '', source: '', month: '', ...(initialFilters || {}) });
  const [options, setOptions] = useState({ categories: [], sources: [], months: [] });
  const [selected, setSelected] = useState([]);
  const [openEntry, setOpenEntry] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/v2/finance/projects/${projectId}/journal/filters`)
      .then((res) => setOptions(res.data))
      .catch(() => {});
  }, [projectId]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
        sort_by: sort.by,
        sort_dir: sort.dir,
      };
      if (search.trim()) params.search = search.trim();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const res = await api.get(`/admin/v2/finance/projects/${projectId}/journal`, { params });
      setRows(res.data.entries || []);
      setTotal(res.data.total || 0);
      setTotals(res.data.totals || { budget: 0, actual: 0 });
    } catch (err) {
      toast.error('Failed to load the journal');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, page, sort, search, filters]);

  useEffect(() => {
    const timer = setTimeout(fetchRows, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [fetchRows, search]);

  const handleSort = (field) => {
    setSort((prev) => ({ by: field, dir: prev.by === field && prev.dir === 'desc' ? 'asc' : 'desc' }));
    setPage(0);
  };

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value === '__all' ? '' : value }));
    setPage(0);
    setSelected([]);
  };

  const activeChips = useMemo(() => {
    const chips = [];
    const labels = {
      status: (v) => (v.includes(',') ? 'Awaiting approval' : STATUS_PILLS[v]?.label || v),
      entry_type: (v) => (v === 'budget' ? 'Budget' : 'Actual'),
      category: (v) => v,
      phase: (v) => (v === UNASSIGNED_PHASE.value ? UNASSIGNED_PHASE.label : phaseLabel(v, projectType)),
      source: (v) => SOURCE_LABELS[v] || v,
      month: (v) => monthLabel(v),
    };
    Object.entries(filters).forEach(([key, value]) => {
      if (value) chips.push({ key, label: labels[key] ? labels[key](value) : value });
    });
    if (search.trim()) chips.push({ key: 'search', label: `"${search.trim()}"` });
    return chips;
  }, [filters, search]);

  const clearChip = (key) => {
    if (key === 'search') return setSearch('');
    setFilter(key, '__all');
  };

  const clearAll = () => {
    setSearch('');
    setFilters({ status: '', entry_type: '', category: '', phase: '', source: '', month: '' });
    setPage(0);
  };

  const toggleAll = () => setSelected(selected.length === rows.length ? [] : rows.map((r) => r.id));
  const toggleOne = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const runBulk = async (action, extra = {}) => {
    if (action === 'delete' && !window.confirm(`Delete ${selected.length} entr${selected.length === 1 ? 'y' : 'ies'}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const res = await api.post(`/admin/v2/finance/projects/${projectId}/entries/bulk`, {
        entry_ids: selected,
        action,
        ...extra,
      });
      const count = res.data.updated ?? res.data.deleted ?? 0;
      toast.success(
        action === 'approve'
          ? `${count} entr${count === 1 ? 'y' : 'ies'} approved and now counted in actuals`
          : action === 'delete'
            ? `${count} entr${count === 1 ? 'y' : 'ies'} deleted`
            : `${count} entr${count === 1 ? 'y' : 'ies'} updated`
      );
      setSelected([]);
      fetchRows();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    downloadCsv(
      `journal-${projectId}.csv`,
      toCsv(
        [
          { label: 'Date', value: (r) => r.entry_date },
          { label: 'Description', value: (r) => r.description },
          { label: 'Type', value: (r) => r.entry_type },
          { label: 'Category', value: (r) => r.category || '' },
          { label: 'Phase', value: (r) => (r.phase ? phaseLabel(r.phase, projectType) : '') },
          { label: 'Account', value: (r) => `${r.account_code} ${r.account_name}` },
          { label: 'Counterparty', value: (r) => r.counterparty || '' },
          { label: 'Reference', value: (r) => r.reference || '' },
          { label: 'Amount', value: (r) => r.amount },
          { label: 'Status', value: (r) => r.status },
          { label: 'Source', value: (r) => r.source },
        ],
        rows
      )
    );
  };

  const pages = Math.ceil(total / PAGE_SIZE) || 1;

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search transactions..."
            className="pl-9 h-9"
            data-testid="journal-search"
          />
        </div>

        {[
          { key: 'entry_type', placeholder: 'Type', all: 'All types', items: [['budget', 'Budget'], ['actual', 'Actual']] },
          {
            key: 'status',
            placeholder: 'Status',
            all: 'All statuses',
            items: [
              // Draft and pending are the same thing to an admin: not yet in the actuals
              ['draft,pending_approval', 'Awaiting approval'],
              ['posted', 'Approved'],
              ['reversed', 'Reversed'],
            ],
          },
          { key: 'category', placeholder: 'Category', all: 'All categories', items: options.categories.map((c) => [c, c]) },
          {
            key: 'phase',
            placeholder: 'Phase',
            all: 'All phases',
            items: [
              ...phaseOptions(projectType).map((p) => [p.value, p.label]),
              [UNASSIGNED_PHASE.value, UNASSIGNED_PHASE.label],
            ],
          },
          { key: 'month', placeholder: 'Month', all: 'All months', items: (options.months || []).map((m) => [m, monthLabel(m)]) },
          { key: 'source', placeholder: 'Source', all: 'All sources', items: (options.sources || []).map((s) => [s, SOURCE_LABELS[s] || s]) },
        ].map(({ key, placeholder, items, all }) => (
          <Select key={key} value={filters[key] || '__all'} onValueChange={(v) => setFilter(key, v)}>
            <SelectTrigger className="h-9 w-auto min-w-[120px]" data-testid={`journal-filter-${key}`}>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">{all}</SelectItem>
              {items.map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}

        <Button variant="outline" size="sm" className="h-9" onClick={exportCsv} data-testid="journal-export">
          Export
        </Button>
      </div>

      {activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="journal-filter-chips">
          {activeChips.map((chip) => (
            <button
              key={chip.key}
              onClick={() => clearChip(chip.key)}
              className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-black/[0.05] text-xs hover:bg-black/[0.08]"
              data-testid={`journal-chip-${chip.key}`}
            >
              {chip.label}
              <X className="h-3 w-3" />
            </button>
          ))}
          <button onClick={clearAll} className="text-xs text-foreground-muted hover:text-foreground underline" data-testid="journal-clear-filters">
            Clear all
          </button>
        </div>
      )}

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg bg-primary/5 border border-primary/20" data-testid="journal-bulk-bar">
          <span className="text-sm font-medium">{selected.length} selected</span>
          <Button size="sm" className="h-8" disabled={busy} onClick={() => runBulk('approve')} data-testid="journal-bulk-approve">
            <CheckCircle className="h-4 w-4 mr-1" /> Approve
          </Button>
          <Select onValueChange={(v) => runBulk('set_phase', { phase: v })}>
            <SelectTrigger className="h-8 w-[150px]" data-testid="journal-bulk-phase">
              <SelectValue placeholder="Set phase" />
            </SelectTrigger>
            <SelectContent>
              {phaseOptions(projectType).map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select onValueChange={(v) => runBulk('set_category', { category: v })}>
            <SelectTrigger className="h-8 w-[165px]" data-testid="journal-bulk-category">
              <SelectValue placeholder="Set category" />
            </SelectTrigger>
            <SelectContent>
              {options.categories.filter((c) => c !== 'Uncategorized').map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" className="h-8 text-red-600" disabled={busy} onClick={() => runBulk('delete')} data-testid="journal-bulk-delete">
            <Trash className="h-4 w-4 mr-1" /> Delete
          </Button>
          <button onClick={() => setSelected([])} className="text-xs text-foreground-muted underline ml-auto">
            Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-black/[0.04] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px]">
            <thead className="bg-black/[0.02] sticky top-0 z-10">
              <tr className="border-b border-black/[0.08]">
                <th className="w-10 py-2 pl-4">
                  <Checkbox
                    checked={rows.length > 0 && selected.length === rows.length}
                    onCheckedChange={toggleAll}
                    data-testid="journal-select-all"
                  />
                </th>
                <SortHeader label="Date" field="entry_date" sort={sort} onSort={handleSort} />
                <SortHeader label="Description" field="description" sort={sort} onSort={handleSort} />
                <SortHeader label="Type" field="entry_type" sort={sort} onSort={handleSort} />
                <SortHeader label="Category" field="category" sort={sort} onSort={handleSort} />
                <SortHeader label="Phase" field="phase" sort={sort} onSort={handleSort} />
                <SortHeader label="Counterparty" field="counterparty" sort={sort} onSort={handleSort} />
                <SortHeader label="Amount" field="amount" sort={sort} onSort={handleSort} align="right" />
                <SortHeader label="Status" field="status" sort={sort} onSort={handleSort} />
                <th className="py-2 px-3 w-10" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="py-10 text-center text-sm text-foreground-muted">Loading transactions…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={10} className="py-10 text-center text-sm text-foreground-muted" data-testid="journal-empty">
                  No transactions match these filters
                </td></tr>
              ) : (
                rows.map((row) => {
                  const pill = STATUS_PILLS[row.status] || STATUS_PILLS.draft;
                  const isDraft = ['draft', 'pending_approval'].includes(row.status);
                  return (
                    <tr
                      key={row.id}
                      className={cn(
                        'border-b border-black/[0.04] hover:bg-black/[0.015] cursor-pointer',
                        isDraft && 'bg-amber-50/30'
                      )}
                      onClick={() => setOpenEntry(row)}
                      data-testid={`journal-row-${row.id}`}
                    >
                      <td className="py-2.5 pl-4" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selected.includes(row.id)}
                          onCheckedChange={() => toggleOne(row.id)}
                          data-testid={`journal-select-${row.id}`}
                        />
                      </td>
                      <td className="py-2.5 px-3 text-sm tabular-nums whitespace-nowrap">{row.entry_date}</td>
                      <td className="py-2.5 px-3 text-sm max-w-[260px]">
                        <div className="truncate">{row.description}</div>
                        <div className="text-xs text-foreground-muted truncate">
                          {row.account_code} · {row.account_name}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-sm capitalize">{row.entry_type}</td>
                      <td className="py-2.5 px-3 text-sm">{row.category || '—'}</td>
                      <td className="py-2.5 px-3">
                        <Badge className={cn('text-[10px]', (row.phase ? phaseMeta(row.phase) : UNASSIGNED_PHASE).chip)}>
                          {row.phase ? phaseLabel(row.phase, projectType) : UNASSIGNED_PHASE.label}
                        </Badge>
                      </td>
                      <td className="py-2.5 px-3 text-sm">{row.counterparty || '—'}</td>
                      <td className="py-2.5 px-3 text-sm text-right tabular-nums font-medium whitespace-nowrap">
                        {money(row.amount, { currency, decimals: 2 })}
                      </td>
                      <td className="py-2.5 px-3">
                        <Badge variant="outline" className={cn('text-[10px] border', pill.className)}>{pill.label}</Badge>
                      </td>
                      <td className="py-2.5 px-3 text-foreground-muted">
                        {row.reference ? <Paperclip className="h-3.5 w-3.5" /> : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-t border-black/[0.05] text-xs text-foreground-muted">
          <span data-testid="journal-footer-totals">
            {total} transaction{total === 1 ? '' : 's'} · Costs {money(totals.costs?.actual, { currency })} of{' '}
            {money(totals.costs?.budget, { currency })} planned
            {(totals.income?.budget || totals.income?.actual) ? (
              <> · Revenue {money(totals.income?.actual, { currency })} of {money(totals.income?.budget, { currency })} planned</>
            ) : null}
            {' '}
            <span className="text-foreground-muted/80">
              (every listed row, drafts included - the Overview actuals count approved entries only)
            </span>
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-7" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="journal-prev">
              Previous
            </Button>
            <span>Page {page + 1} of {pages}</span>
            <Button variant="ghost" size="sm" className="h-7" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} data-testid="journal-next">
              Next
            </Button>
          </div>
        </div>
      </div>

      {openEntry && (
        <JournalEntryPanel
          entry={openEntry}
          projectId={projectId}
          projectType={projectType}
          currency={currency}
          onClose={() => setOpenEntry(null)}
          onChanged={() => { fetchRows(); onChanged?.(); }}
          onEdit={onEdit ? (entry) => { setOpenEntry(null); onEdit(entry); } : undefined}
        />
      )}
    </div>
  );
};

export default FinanceJournal;
